import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Typography } from "@mui/material";
import {
  CheckCircleOutlineRounded,
  EditNoteOutlined,
  ReportProblemOutlined,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import { DocumentCraftingFinding } from "../../../Models/documentCrafting";
import { StatusChip } from "./DocumentCraftingUi";
import { uiLanguage } from "./useDocumentCraftingEnumLabels";
import { PreviewUnit } from "./PagePreview";

const PLACEHOLDER = /\{\{\s*([\w.]+)\s*\}\}/g;
// Params carry full texts; quoted snippets stay short.
const MAX_PARAM_CHARS = 60;

const paramText = (value: unknown): string | number => {
  if (Array.isArray(value)) return value.map(paramText).join(", ");
  if (typeof value === "number") return value;
  if (value === null || value === undefined) return "";
  const text =
    typeof value === "object" ? JSON.stringify(value) : String(value);
  return text.length > MAX_PARAM_CHARS
    ? `${text.slice(0, MAX_PARAM_CHARS).trimEnd()}…`
    : text;
};

/**
 * Texts of checker findings. The message comes from `checks.<message_key>` of
 * the UI language when every placeholder of that text has a parameter;
 * otherwise from the server (`message_de` / `message_en`).
 */
export const useFindingText = () => {
  const { t, i18n } = useTranslation("documentCrafting");
  const language = uiLanguage(i18n.language);

  const message = (finding: DocumentCraftingFinding) => {
    const server =
      (language === "de" ? finding.message_de : finding.message_en) ||
      finding.message_de ||
      finding.message_en;
    const key = `checks.${finding.message_key}`;
    const template = i18n.getResource(language, "documentCrafting", key);
    if (typeof template !== "string") return server;

    const params: Record<string, string | number> = {};
    Object.keys(finding.params ?? {}).forEach((name) => {
      params[name] = paramText(finding.params[name]);
    });
    if (finding.page !== null) params.page ??= finding.page;
    if (finding.slide !== null) params.slide ??= finding.slide;
    if (finding.element) params.element ??= finding.element;

    let complete = true;
    template.replace(PLACEHOLDER, (match: string, name: string) => {
      if (!(name in params)) complete = false;
      return match;
    });
    return complete ? t(key, { ...params, lng: language }) : server;
  };

  // "a4.umfang.overflow" -> checks.names.umfang
  const checkName = (finding: DocumentCraftingFinding) => {
    const segment = finding.message_key.split(".")[1];
    return segment
      ? t(`checks.names.${segment}`, { defaultValue: finding.check })
      : finding.check;
  };

  return { message, checkName };
};

interface Props {
  findings: DocumentCraftingFinding[];
  /** A4 pages or presentation slides (group titles). */
  unit?: PreviewUnit;
  /** Label of a form field key, from the type's form schema. */
  fieldLabel?: (formField: string) => string;
  /** Opens the revise form with this field focused; without it the notes
   * only name the field. */
  onEditField?: (formField: string) => void;
}

const FindingRow: React.FC<{
  finding: DocumentCraftingFinding;
  fieldLabel?: (formField: string) => string;
  onEditField?: (formField: string) => void;
}> = ({ finding, fieldLabel, onEditField }) => {
  const { t } = useTranslation("documentCrafting");
  const text = useFindingText();
  const isInput = finding.severity === "input";
  const Icon = isInput ? EditNoteOutlined : ReportProblemOutlined;

  return (
    <Box
      component="li"
      sx={{
        display: "flex",
        gap: 1.25,
        py: 1.25,
        borderBottom: `1px solid ${color.gray100}`,
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Icon
        sx={{
          fontSize: 20,
          mt: "1px",
          flexShrink: 0,
          color: isInput ? color.blue700 : color.orange600,
        }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: color.gray500,
          }}
        >
          {text.checkName(finding)}
        </Typography>
        <Typography
          sx={{ fontSize: 14, color: color.gray900, overflowWrap: "anywhere" }}
        >
          {text.message(finding)}
        </Typography>
        {isInput && finding.form_field && (
          <Box
            sx={{
              mt: 0.75,
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontSize: 12, color: color.gray600 }}>
              {t("common.quality.field", {
                field: fieldLabel?.(finding.form_field) ?? finding.form_field,
              })}
            </Typography>
            {onEditField && (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => onEditField(finding.form_field as string)}
                sx={{ py: 0, minHeight: 26 }}
              >
                {t("common.quality.editField")}
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

/**
 * The checker findings left on a revision, grouped by page (slide).
 * "Needs your input" findings name the form field to fill.
 */
const QualityNotes: React.FC<Props> = ({
  findings,
  unit = "page",
  fieldLabel,
  onEditField,
}) => {
  const { t } = useTranslation("documentCrafting");

  if (!findings.length) {
    return (
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, color: color.green700 }}
      >
        <CheckCircleOutlineRounded sx={{ fontSize: 20 }} />
        <Typography sx={{ fontSize: 14, color: color.gray700 }}>
          {t("common.quality.none")}
        </Typography>
      </Box>
    );
  }

  const where = (finding: DocumentCraftingFinding) =>
    unit === "slide"
      ? (finding.slide ?? finding.page)
      : (finding.page ?? finding.slide);
  const groups: { position: number | null; items: DocumentCraftingFinding[] }[] =
    [];
  findings.forEach((finding) => {
    const position = where(finding) ?? null;
    const group = groups.find((item) => item.position === position);
    if (group) group.items.push(finding);
    else groups.push({ position, items: [finding] });
  });
  // General notes first, then by page.
  groups.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  const inputCount = findings.filter((f) => f.severity === "input").length;

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
        <StatusChip
          label={t("common.quality.count", { count: findings.length })}
          tone="orange"
        />
        {inputCount > 0 && (
          <StatusChip
            label={t("common.quality.inputCount", { count: inputCount })}
            tone="blue"
          />
        )}
      </Box>
      {groups.map((group) => (
        <Box key={group.position ?? "general"} sx={{ mb: 1.5 }}>
          <Typography
            sx={{ fontSize: 13, fontWeight: 700, color: color.gray700, mb: 0.25 }}
          >
            {group.position === null
              ? t("common.quality.general")
              : t(`common.quality.${unit}`, { n: group.position })}
          </Typography>
          <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
            {group.items.map((finding, index) => (
              <FindingRow
                key={`${finding.message_key}-${index}`}
                finding={finding}
                fieldLabel={fieldLabel}
                onEditField={onEditField}
              />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default QualityNotes;
